import type { Director } from 'contexttrees';
import type { DesignMapNode } from 'contexttrees';
import { AGENT_PERSONAS } from '../agent/personas';
import { getActiveContextTreesProjectId, listContextTreesProjects, loadContextTreesDirector } from '../db/contexttrees-repo';
import { openModal } from './modal';

/**
 * Read-only window onto the active ContextTrees project's *persistent* state — the design map and
 * per-agent retained memory as stored, not the decayed/scoped slice any one session was handed.
 *
 * Deliberately has no edit, apply, or node-creation controls: authoring the design map from the UI
 * is a later phase (see `CONTEXTTREES_INTEGRATION.md`), and a half-built editor here would be a
 * second, competing write path against session-end reconciliation.
 */
export async function showContextTreesOverview(): Promise<void> {
  const modal = openModal('Project Overview');

  const status = document.createElement('div');
  status.className = 'ct-overview-note';
  status.textContent = 'Loading project…';
  modal.bodyEl.appendChild(status);

  try {
    const projectId = await getActiveContextTreesProjectId();
    if (!projectId) {
      status.textContent = 'Turn on Project Mode to start a project and see its roadmap and agent context here.';
      return;
    }

    const [projects, director] = await Promise.all([listContextTreesProjects(), loadContextTreesDirector(projectId)]);
    const project = projects.find((p) => p.id === projectId);

    status.remove();
    modal.bodyEl.appendChild(renderProjectLine(project?.name ?? projectId));
    modal.bodyEl.appendChild(renderRoadmap(director));
    modal.bodyEl.appendChild(renderDirectorContext(director));
    modal.bodyEl.appendChild(renderAgentContext(director));
  } catch (err) {
    // A modal that throws leaves the user staring at "Loading project…" forever; say what happened.
    status.classList.add('error');
    status.textContent = `Could not load the project: ${err instanceof Error ? err.message : String(err)}`;
  }
}

function renderProjectLine(name: string): HTMLElement {
  const line = document.createElement('p');
  line.className = 'ct-overview-intro';
  line.textContent = `${name} — read-only view of the stored design map and agent memory.`;
  return line;
}

// ---------- 1. Roadmap (the design-map tree) ----------

function renderRoadmap(director: Director): HTMLElement {
  const section = makeSection('Roadmap');
  const nodes = director.designMap.allNodes();

  if (nodes.length === 0) {
    section.appendChild(makeNote('No design-map nodes yet.'));
    return section;
  }

  const byParent = new Map<string | null, DesignMapNode[]>();
  const ids = new Set(nodes.map((node) => node.id));
  for (const node of nodes) {
    // An unknown parentId would otherwise make a node invisible — treat it as a root instead.
    const key = node.parentId && ids.has(node.parentId) ? node.parentId : null;
    byParent.set(key, [...(byParent.get(key) ?? []), node]);
  }

  const tree = document.createElement('div');
  tree.className = 'ct-overview-tree';
  // Guards against a parentId cycle in stored data turning this into an infinite walk.
  const seen = new Set<string>();

  const walk = (node: DesignMapNode, depth: number) => {
    if (seen.has(node.id)) return;
    seen.add(node.id);
    tree.appendChild(renderTreeRow(director, node, depth));
    for (const child of byParent.get(node.id) ?? []) walk(child, depth + 1);
  };
  for (const root of byParent.get(null) ?? []) walk(root, 0);

  section.appendChild(tree);
  return section;
}

function renderTreeRow(director: Director, node: DesignMapNode, depth: number): HTMLElement {
  const row = document.createElement('div');
  row.className = 'ct-overview-tree-row';
  row.style.paddingLeft = `${depth * 14}px`;

  const name = document.createElement('span');
  name.className = 'ct-overview-tree-name';
  name.textContent = node.name;
  row.appendChild(name);

  const active = director.designMap.isActive(node.id);
  row.appendChild(makeChip(node.kind));
  row.appendChild(makeChip(safeStatus(director, node.id)));
  row.appendChild(makeChip(active ? 'active' : 'dormant', active ? 'good' : 'muted'));

  return row;
}

// ---------- 2. Director (all context) ----------

function renderDirectorContext(director: Director): HTMLElement {
  const section = makeSection('Director (all context)');
  section.appendChild(
    makeNote('Everything stored on every active node, in full — not the decayed slice a single session receives.'),
  );

  const active = director.designMap.allNodes().filter((node) => director.designMap.isActive(node.id));
  if (active.length === 0) {
    section.appendChild(makeNote('No active nodes.'));
    return section;
  }

  for (const node of active) {
    const card = document.createElement('div');
    card.className = 'ct-overview-node';

    const head = document.createElement('div');
    head.className = 'ct-overview-node-head';
    const title = document.createElement('span');
    title.className = 'ct-overview-node-title';
    title.textContent = node.name;
    head.appendChild(title);
    head.appendChild(makeChip(safeStatus(director, node.id)));
    card.appendChild(head);

    const content = safeCurrent(director, node.id);
    const summary = document.createElement('div');
    summary.className = 'ct-overview-summary';
    summary.textContent = content?.summary?.trim() || 'No summary recorded.';
    card.appendChild(summary);

    if (content) {
      card.appendChild(makeList('Roadmap', content.roadmap));
      card.appendChild(makeList('Bugs', content.bugs));
      card.appendChild(makeList('Future review', content.futureReview));
    }

    section.appendChild(card);
  }

  return section;
}

// ---------- 3. Per-agent context ----------

function renderAgentContext(director: Director): HTMLElement {
  const section = makeSection('Per-agent context');
  const harnesses = director.harnesses.all();

  if (harnesses.length === 0) {
    section.appendChild(makeNote('No agents have run against this project yet.'));
    return section;
  }

  for (const harness of harnesses) {
    // Harness ids are minted from persona ids, so a persona lookup gives the label the rest of the
    // app shows. Anything registered outside that path falls back to its own name.
    const label = AGENT_PERSONAS.find((persona) => persona.id === harness.id)?.label ?? harness.name;

    const block = document.createElement('div');
    block.className = 'ct-overview-agent';

    const head = document.createElement('div');
    head.className = 'ct-overview-agent-head';
    const nameEl = document.createElement('span');
    nameEl.className = 'ct-overview-agent-name';
    nameEl.textContent = label;
    head.appendChild(nameEl);
    const idEl = document.createElement('span');
    idEl.className = 'ct-overview-agent-id mono';
    idEl.textContent = harness.id;
    head.appendChild(idEl);
    block.appendChild(head);

    const groups = director.agentMemories.get(harness.id)?.all() ?? [];
    if (groups.length === 0) {
      block.appendChild(makeNote('Nothing retained yet.'));
    } else {
      for (const group of groups) {
        const item = document.createElement('div');
        item.className = 'ct-overview-memory';

        const concept = document.createElement('div');
        concept.className = 'ct-overview-memory-concept';
        concept.textContent = group.concept;
        item.appendChild(concept);

        const body = document.createElement('div');
        body.className = 'ct-overview-memory-content';
        body.textContent = group.content;
        item.appendChild(body);

        const meta = document.createElement('div');
        meta.className = 'ct-overview-memory-meta mono';
        meta.textContent = `relevance ${group.relevanceScore.toFixed(2)} · reuse ${group.reuseScore.toFixed(2)}`;
        item.appendChild(meta);

        block.appendChild(item);
      }
    }

    section.appendChild(block);
  }

  return section;
}

// ---------- Shared bits ----------

/** `current`/`requireNode` throw on an unknown id; one bad node shouldn't blank the whole modal. */
function safeCurrent(director: Director, nodeId: string) {
  try {
    return director.designMap.current(nodeId);
  } catch {
    return undefined;
  }
}

function safeStatus(director: Director, nodeId: string): string {
  return safeCurrent(director, nodeId)?.status ?? 'unknown';
}

function makeSection(label: string): HTMLElement {
  const section = document.createElement('section');
  section.className = 'ct-overview-section';
  const labelEl = document.createElement('div');
  labelEl.className = 'ct-overview-section-label';
  labelEl.textContent = label;
  section.appendChild(labelEl);
  return section;
}

function makeNote(text: string): HTMLElement {
  const note = document.createElement('div');
  note.className = 'ct-overview-note';
  note.textContent = text;
  return note;
}

function makeChip(text: string, tone: 'plain' | 'good' | 'muted' = 'plain'): HTMLElement {
  const chip = document.createElement('span');
  chip.className = `chip ct-overview-chip${tone === 'plain' ? '' : ` ${tone}`}`;
  chip.textContent = text;
  return chip;
}

function makeList(label: string, items: string[]): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'ct-overview-list';

  const labelEl = document.createElement('div');
  labelEl.className = 'ct-overview-list-label';
  labelEl.textContent = `${label} (${items.length})`;
  wrapper.appendChild(labelEl);

  if (items.length === 0) {
    wrapper.appendChild(makeNote('None.'));
    return wrapper;
  }

  const list = document.createElement('ul');
  for (const item of items) {
    const li = document.createElement('li');
    li.textContent = item;
    list.appendChild(li);
  }
  wrapper.appendChild(list);
  return wrapper;
}
